import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { produce } from 'immer';

import {
  Dimension,
  ExperimentAssignmentStrategy,
  EXPERIMENTS_DEFAULT_PAGE_SIZE,
  getAssignmentStrategy,
  isTimeSliceShuffleStrategy,
  isTimeSliceStrategy,
  MetricType,
  MetricWithDimensionFields,
  TreatmentDefaultColors,
  TreatmentIds,
} from '../constants';
import {
  Choice,
  Experiment,
  RegionalExperimentConfigValues,
  Variable,
} from '../domain/experiment';
import { AllocationData } from './types';
import {
  EXPERIMENTS_PARAM_END,
  EXPERIMENTS_PARAM_PAGENO_KEY,
  EXPERIMENTS_PARAM_PAGESIZE_KEY,
  EXPERIMENTS_PARAM_SEARCH_KEY,
  EXPERIMENTS_PARAM_START,
  EXPERIMENTS_VIEW,
} from '@/common/const';
import {
  ABORTED,
  CANCELLED,
  COMPLETED,
  RUNNING,
  SCHEDULED,
  STARTED,
} from '@/constants';
import {
  EXP_ELIGIBLE_FOR_EDITING_CUTOFF_DATE,
  TIMEZONE,
} from '@/constants/dateTime';
import { FACETS } from '@/constants/facets';
import { SliceAssignment } from '@/lib/PlannerDTOs';
import {
  extractFiltersFromSearchParams,
  extractParamValueByName,
} from '@/utils/url';

dayjs.extend(utc);
dayjs.extend(timezone);

export type ExperimentSearchData = {
  searchVal: string;
  pageNumber: number;
  pageSize: number;
  filters: { [key: string]: any };
};

export function extractExperimentSearchData(
  searchUrl: string,
): ExperimentSearchData {
  const searchVal =
    extractParamValueByName(searchUrl, EXPERIMENTS_PARAM_SEARCH_KEY) || '';
  const blacklist = [
    EXPERIMENTS_PARAM_SEARCH_KEY,
    EXPERIMENTS_PARAM_PAGENO_KEY,
    EXPERIMENTS_PARAM_PAGESIZE_KEY,
    EXPERIMENTS_PARAM_START,
    EXPERIMENTS_PARAM_END,
    EXPERIMENTS_VIEW,
  ];
  const filters = extractFiltersFromSearchParams(searchUrl, blacklist);
  let pageNo =
    extractParamValueByName(searchUrl, EXPERIMENTS_PARAM_PAGENO_KEY) || 1;
  let pageSize =
    extractParamValueByName(searchUrl, EXPERIMENTS_PARAM_PAGESIZE_KEY) ||
    EXPERIMENTS_DEFAULT_PAGE_SIZE;

  if (pageNo) pageNo = parseInt(pageNo as string, 10);
  if (pageSize) pageSize = parseInt(pageSize as string, 10);

  return {
    searchVal,
    pageNumber: pageNo as number,
    pageSize: pageSize as number,
    filters,
  };
}

const calculateAllocationItem = (
  unixStart: number,
  sliceAssginmentUnixStart: number,
  sliceAssignmentUnixEnd: number,
  blockCountEachHour: number,
) => {
  const startTimestampObj = dayjs.unix(unixStart);
  const itemStartObj = dayjs.unix(sliceAssginmentUnixStart);

  const itemStartOfDay = itemStartObj.startOf('day');
  const startTimeStartOfDay = startTimestampObj.startOf('day');

  const dayIndex = itemStartOfDay.diff(startTimeStartOfDay, 'day');

  const blockStartIndex = Math.round(
    itemStartObj.diff(itemStartOfDay, 'minute') / (60 / blockCountEachHour),
  );

  const span = Math.round(
    dayjs
      .unix(sliceAssignmentUnixEnd)
      .diff(dayjs.unix(sliceAssginmentUnixStart), 'minutes') /
      60 /
      (1 / blockCountEachHour),
  );

  return { dayIndex, blockStartIndex, span };
};

export const convertSliceAssignmentsToAllocationData = (
  sliceAssignments: SliceAssignment[],
  unixStart: number,
  blockCountEachHour: number,
): AllocationData[] => {
  const allocationData = [];
  sliceAssignments.forEach((sliceAssignment) => {
    const treatmentId =
      TreatmentIds[sliceAssignment.choice_index] || TreatmentIds[0];

    const { dayIndex, blockStartIndex, span } = calculateAllocationItem(
      unixStart,
      sliceAssignment.start,
      sliceAssignment.end,
      blockCountEachHour,
    );

    const maxBlockEachDay = 24 * blockCountEachHour;
    const isSpillingToNextDay = blockStartIndex + span - maxBlockEachDay > 0;

    let allocationDataToInsert: { dayIndex: number; blockIndexes: number[] }[] =
      [];
    if (!isSpillingToNextDay) {
      allocationDataToInsert = [
        {
          dayIndex,
          blockIndexes: Array.from(
            { length: span },
            (_, i) => blockStartIndex + i,
          ),
        },
      ];
    } else {
      allocationDataToInsert = [
        {
          dayIndex,
          blockIndexes: Array.from(
            { length: maxBlockEachDay - blockStartIndex },
            (_, i) => blockStartIndex + i,
          ),
        },
        {
          dayIndex: dayIndex + 1,
          blockIndexes: Array.from(
            { length: span - (maxBlockEachDay - blockStartIndex) },
            (_, i) => i,
          ),
        },
      ];
    }

    allocationDataToInsert.forEach((allocationDataItemToInsert) => {
      const treatmentAllocationData = allocationData.find(
        (treatmentData) => treatmentData.name === treatmentId,
      );

      if (!treatmentAllocationData) {
        allocationData.push({
          name: treatmentId,
          color: TreatmentDefaultColors[sliceAssignment.choice_index],
          allocation: [
            {
              dayIndex: allocationDataItemToInsert.dayIndex,
              blockIndexes: [...allocationDataItemToInsert.blockIndexes],
            },
          ],
        });
      } else {
        const dayData = treatmentAllocationData.allocation.find(
          (allocationItem) =>
            allocationItem.dayIndex === allocationDataItemToInsert.dayIndex,
        );
        if (dayData) {
          allocationDataItemToInsert.blockIndexes.forEach((blockIndex) => {
            if (!dayData.blockIndexes.includes(blockIndex)) {
              dayData.blockIndexes.push(blockIndex);
            }
          });
        } else {
          treatmentAllocationData.allocation.push({
            dayIndex: allocationDataItemToInsert.dayIndex,
            blockIndexes: [...allocationDataItemToInsert.blockIndexes],
          });
        }
      }
    });
  });

  // sort by treatment id in the TreatmentIds
  const sortedAllocationData = allocationData.sort((a, b) => {
    const aIndex = TreatmentIds.indexOf(a.name);
    const bIndex = TreatmentIds.indexOf(b.name);
    return aIndex - bIndex;
  });

  return sortedAllocationData;
};

export const constructDimensionFields = (
  metric,
): MetricWithDimensionFields => ({
  ...metric,
  dimension_filters: [
    {
      dimension_filter_constraints:
        metric.dimension_filters?.[0]?.dimension_filter_constraints || [],
    },
  ],
  join_strategy: metric?.join_strategy,
  metric_default_value:
    metric?.join_strategy && metric?.metric_default_value
      ? metric?.metric_default_value ?? 0
      : undefined,
});

export const normalizeMetricDataFromServer = (
  metric: Partial<MetricWithDimensionFields>,
): MetricWithDimensionFields | null => {
  return constructDimensionFields(metric);
};

export const normalizeMetrics = (
  metrics: MetricWithDimensionFields[] | null,
) => {
  return metrics?.map(normalizeMetricDataFromServer)?.filter(Boolean) || [];
};

export const getDimensionsFromAnalysisConfig = (
  rawAnalysisConfig: any,
  metricName: string,
): Dimension[] => {
  if (!rawAnalysisConfig) {
    return [];
  }

  // Search for the metric in all metric types (primary, secondary, guardrail, control)
  const allMetrics = [
    ...(rawAnalysisConfig.primary_metrics || []),
    ...(rawAnalysisConfig.secondary_metrics || []),
    ...(rawAnalysisConfig.guardrail_metrics || []),
    ...(rawAnalysisConfig.control_metrics || []),
  ];

  const metric = allMetrics.find((m) => m.name === metricName);

  if (!metric || !metric.deepdive_dimensions) {
    return [];
  }

  return metric.deepdive_dimensions.map((d) => ({
    name: d.dimension_name,
    display_name: d.dimension_display_name,
    data_type: 'STRING' as const,
    cardinality: 'LOW' as const,
    governed_dimension_name: d.dimension_name,
  }));
};

export const getMetricTypePrefix = (metricType: MetricType): string => {
  switch (metricType) {
    case 'primary':
      return '[P0]';
    case 'secondary':
      return '[P1]';
    case 'guardrail':
      return '[G]';
    default:
      return '';
  }
};

export const validPercentageChoice = (choices: Choice[]): boolean => {
  const expectedVal = Math.round(100 / choices.length);

  return choices.every((choice) => choice.chance === expectedVal);
};

export const validWeightedChoiceTotal = (choices: Choice[]): boolean => {
  let total = 0;
  choices.forEach((choice) => {
    total += choice.chance;
  });
  return total === 100;
};

export const parseExperiment = (
  changedValues: any,
  experiment: Experiment,
): Experiment => {
  const nextExperiment = produce(experiment, (draft) => {
    Object.keys(changedValues).forEach((key) => {
      if (key === 'dayLength') {
        if (!changedValues[key]) {
          draft.endTime = null;
        } else if (draft.startTime) {
          draft.endTime = draft.startTime
            .add(changedValues[key], 'days')
            .subtract(1, 'minutes');
        }

        const constraints = draft?.constraints || [];

        const endConstraint = constraints.find(
          (constraint) =>
            constraint.target === FACETS.Timestamp && constraint.op === '<',
        );

        const unixEndTime = draft.endTime ? draft.endTime.unix() : null;

        if (endConstraint) {
          endConstraint.value = unixEndTime ? unixEndTime.toString() : null;
        } else {
          constraints.push({
            target: FACETS.Timestamp,
            op: '<',
            value: unixEndTime ? unixEndTime.toString() : null,
          });
        }

        draft.constraints = constraints;
      }
    });
  });

  return nextExperiment;
};

export const parseConfiguration = (changedValues: any, allFields: any) => {
  const nextConfigurations = produce(allFields.configurations, (draft) => {
    changedValues?.configurations?.[0]?.variables?.[0]?.choices?.forEach(
      (choice: Choice) => {
        if (choice.chance)
          draft.forEach((configuration: RegionalExperimentConfigValues) => {
            configuration?.variables?.forEach((variable: Variable) => {
              variable.strategy = ExperimentAssignmentStrategy.WEIGHTED_CHOICE;
            });
          });
      },
    );
  });

  return nextConfigurations;
};

export const getExperimentByCountryCode = (
  experiments: Experiment[],
  countryCode: string,
): Experiment => {
  return experiments.find((experiment) => {
    const experimentName = experiment.name;
    const experimentNameParts = experimentName.split('_');

    if (experimentNameParts.length < 2) {
      return false;
    }

    return countryCode === experimentNameParts[experimentNameParts.length - 2];
  });
};

export const isValidMetric = (metric: MetricWithDimensionFields): boolean => {
  if (typeof metric?.name === 'string') {
    return metric.name.trim().length > 0;
  }
  return !!metric?.name;
};

/**
 * Terminal states for experiments (completed/aborted/cancelled)
 */
export const TERMINAL_STATES: readonly string[] = [
  COMPLETED,
  ABORTED,
  CANCELLED,
];

/**
 * States after experiment has started (running or terminal)
 */
export const POST_STARTED_STATES: readonly string[] = [
  STARTED,
  RUNNING,
  COMPLETED,
  ABORTED,
  CANCELLED,
];

/**
 * Checks if an experiment uses a timeslice assignment strategy.
 *
 * @param experiment - The experiment to check
 * @returns boolean indicating if the experiment is a timeslice experiment
 */
export const isTimesliceExperiment = (
  experiment: Pick<Experiment, 'variables'> | null | undefined,
): boolean => {
  const strategy = experiment?.variables?.[0]?.strategy;
  return !!strategy && isTimeSliceStrategy(strategy);
};

/**
 * Checks if an experiment is eligible for post-completion editing.
 * Eligibility criteria:
 * - Experiment was created after the EXP_ELIGIBLE_FOR_EDITING_CUTOFF_DATE
 * - Experiment is in a terminal state (COMPLETED, ABORTED, CANCELLED)
 * - Experiment ended within the last 30 days
 * - Experiment is NOT a timeslice experiment (timeslice experiments are excluded)
 *
 * @param experiment - The experiment to check
 * @returns boolean indicating if the experiment can be edited post-completion
 */
export const isEligibleForPostEndEditing = (
  experiment:
    | Pick<Experiment, 'createdAt' | 'state' | 'endTime' | 'variables'>
    | null
    | undefined,
): boolean => {
  if (!experiment?.createdAt) return false;

  // Timeslice experiments are not eligible for post-completion editing
  if (isTimesliceExperiment(experiment)) return false;

  return (
    !dayjs
      .unix(experiment.createdAt)
      .isBefore(dayjs(EXP_ELIGIBLE_FOR_EDITING_CUTOFF_DATE).startOf('day')) &&
    TERMINAL_STATES.includes(experiment.state) &&
    !!experiment.endTime?.isAfter(dayjs().subtract(30, 'day').startOf('day'))
  );
};

// TEMP: Remove once temporary timeslice controlled metrics editing is no longer needed.
/**
 * Checks if a timeslice experiment is eligible for controlled metrics editing
 * before/during the experiment or within the 60-day post-end editing window.
 *
 * @param experiment - The experiment to check
 * @returns boolean indicating if controlled metrics can be edited
 */
export const isTimesliceEligibleForControlledMetricsEditing = (
  experiment:
    | Pick<Experiment, 'state' | 'variables' | 'endTime'>
    | null
    | undefined,
): boolean => {
  if (!experiment) return false;
  if (!isTimesliceExperiment(experiment)) return false;

  if ([STARTED, RUNNING, SCHEDULED].includes(experiment.state)) return true;

  return (
    TERMINAL_STATES.includes(experiment.state) &&
    !!experiment.endTime?.isAfter(dayjs().subtract(60, 'day').startOf('day'))
  );
};

export const AXA_WARNING_MESSAGES = {
  startTime:
    'AXA scorecards are not supported for experiments starting at non-hour timestamps.',
  sliceWindow:
    'AXA scorecards are not supported for minute-based slice windows. Please select an hourly window to enable AXA.',
} as const;

export const AXA_INCOMPATIBLE_OPT_OUT_REASON =
  'Experiment not compatible for AXA Scorecards';

export const isAxaIncompatibleStartTime = (
  time: dayjs.Dayjs | undefined | null,
): boolean =>
  time !== null && time !== undefined && time.tz(TIMEZONE).minute() !== 0;

export const isAxaIncompatibleSliceWindow = (
  durationInSeconds: number | undefined | null,
): boolean =>
  durationInSeconds !== null &&
  durationInSeconds !== undefined &&
  durationInSeconds % 3600 !== 0;

/**
 * Converts a time span to seconds based on its unit.
 * @param span - The time span value
 * @param unit - The time unit ('seconds', 'minutes', or 'hours')
 * @returns The span converted to seconds
 */
const convertSpanToSeconds = (
  span: number | undefined | null,
  unit: string | undefined | null,
): number => {
  if (span === null || span === undefined) return 0;
  switch (unit) {
    case 'hours':
      return span * 3600;
    case 'minutes':
      return span * 60;
    case 'seconds':
      return span;
    default:
      // Log warning for unrecognized units, treat as seconds
      if (unit != null) {
        console.warn(
          `convertSpanToSeconds: Unrecognized time unit "${unit}", treating as seconds`,
        );
      }
      return span;
  }
};

/**
 * Calculates the total slice window duration in seconds for Sequential timeslice experiments.
 * The slice window is the sum of Control (first choice) + Treatment A (second choice) spans.
 * @param configurations - The experiment configurations containing variables and choices
 * @returns The total slice window duration in seconds, or undefined if not applicable
 */
export const getSequentialTimesliceSliceWindow = (
  configurations: RegionalExperimentConfigValues[] | undefined | null,
): number | undefined => {
  const choices = configurations?.[0]?.variables?.[0]?.choices;
  if (!choices || choices.length < 2) return undefined;

  const controlSpan = convertSpanToSeconds(
    choices[0]?.span,
    choices[0]?.spanUnit,
  );
  const treatmentASpan = convertSpanToSeconds(
    choices[1]?.span,
    choices[1]?.spanUnit,
  );

  return controlSpan + treatmentASpan;
};

export interface AxaCompatibilityInfo {
  isAxaIncompatible: boolean;
  sliceWindowDuration: number | undefined;
  isTimeslice: boolean;
}

/**
 * Calculates the AXA compatibility information for an experiment.
 * This centralizes the logic for determining if a timeslice experiment
 * is compatible with AXA scorecards.
 *
 * @param configurations - The experiment configurations
 * @param experiment - The experiment object containing duration and startTime
 * @returns Object with isAxaIncompatible flag and sliceWindowDuration
 */
export const getAxaCompatibilityInfo = (
  configurations: RegionalExperimentConfigValues[] | undefined | null,
  experiment:
    | { duration?: number; startTime?: dayjs.Dayjs | null }
    | undefined
    | null,
): AxaCompatibilityInfo => {
  const strategyName = getAssignmentStrategy(configurations ?? []);
  const isCustomTimeslice =
    strategyName === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE;
  const isSequentialTimeslice = isTimeSliceShuffleStrategy(strategyName);

  // Not a timeslice experiment - always compatible
  if (!isCustomTimeslice && !isSequentialTimeslice) {
    return {
      isAxaIncompatible: false,
      sliceWindowDuration: undefined,
      isTimeslice: false,
    };
  }

  // Calculate slice window duration based on experiment type
  const sliceWindowDuration = isCustomTimeslice
    ? experiment?.duration
    : getSequentialTimesliceSliceWindow(configurations);

  const isAxaIncompatible =
    isAxaIncompatibleStartTime(experiment?.startTime) ||
    isAxaIncompatibleSliceWindow(sliceWindowDuration);

  return { isAxaIncompatible, sliceWindowDuration, isTimeslice: true };
};
